import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronLeft, Phone } from 'lucide-react';
import { MessengerAvatar } from './messengerUtils';

/**
 * Compact chat shell — the redesigned surface. Trades the previous large
 * floating avatar for a sticky top bar so the message thread gets as much
 * viewport as possible. The Allure logo (if any) intentionally lives in the
 * top-right cluster next to the bell — small enough to disappear visually
 * but still there for brand consistency.
 */
export const MessengerChatShell = ({
  pageTestId,
  bellNode,
  bottomNav,
  onBack,
  backTestId,
  contactName,
  contactImage,
  contactSubtitle = '',
  contactTestId,
  showCallButton = false,
  onCall,
  callTestId,
  callBadge = false,
  messages,
  loading,
  emptyText,
  renderMessage,
  composer,
  pinnedNode,
  scrollDependency,
}) => {
  const bottomRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const hasInitialScrolledRef = useRef(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  // On the first render with messages, jump to the bottom INSTANTLY so the
  // thread opens where the user left it — no visible "scroll from top"
  // animation. useLayoutEffect fires before paint so the initial jump is
  // never visible to the user. Subsequent updates (a new incoming/outgoing
  // message) use a smooth scroll so the transition feels alive.
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return undefined;
    // Empty thread — reset the first-jump tracker so navigating between
    // conversations without unmounting still gives the next thread the
    // instant jump instead of a smooth scroll from the top.
    if (!scrollDependency) {
      hasInitialScrolledRef.current = false;
      return undefined;
    }
    const jumpInstant = () => {
      container.scrollTop = container.scrollHeight;
    };
    if (!hasInitialScrolledRef.current) {
      // Scroll the container directly rather than scrollIntoView on the
      // sentinel — that way we always land exactly at scrollHeight instead
      // of one row above it when the newest bubble's meta/status line
      // hasn't finished laying out yet.
      jumpInstant();
      hasInitialScrolledRef.current = true;
      // Re-apply across the next two frames to catch late layout from
      // async media (images, videos) and status rows so the newest
      // bubble is never clipped by the composer.
      const raf1 = requestAnimationFrame(jumpInstant);
      const raf2 = requestAnimationFrame(() => requestAnimationFrame(jumpInstant));
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }
    // Smooth scroll new messages into view. A ResizeObserver (below) keeps
    // us pinned if the just-added row grows after the fact (delivered tick,
    // status row appearing, image finishing decoding).
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    return undefined;
  }, [scrollDependency]);

  // Keep the thread pinned to the bottom whenever the scroll content grows
  // AND the user is already at (or very near) the bottom. Fixes two subtle
  // clipping bugs: (a) the newest bubble's meta/status row layouts a beat
  // after the smooth scroll finishes so the bubble ends up 20-50px above
  // the composer, and (b) images/videos decoding late push the thread
  // upward.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      const distanceFromBottom = container.scrollHeight - (container.scrollTop + container.clientHeight);
      if (distanceFromBottom < 80) {
        container.scrollTop = container.scrollHeight;
      }
    });
    Array.from(container.children).forEach((child) => observer.observe(child));
    // Also observe any children added later (new message bubbles).
    const mutation = new MutationObserver((entries) => {
      entries.forEach((entry) => {
        entry.addedNodes.forEach((node) => {
          if (node.nodeType === 1) observer.observe(node);
        });
      });
    });
    mutation.observe(container, { childList: true });
    return () => {
      observer.disconnect();
      mutation.disconnect();
    };
  }, []);

  // Track whether the user has scrolled up meaningfully — show the floating
  // "jump to latest" arrow when they're > 200px above the newest message.
  const handleScroll = useCallback((event) => {
    const el = event.currentTarget;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    setShowScrollToBottom(distanceFromBottom > 200);
  }, []);

  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, []);

  // Keep the newest messages visible when the soft keyboard appears.
  // Android has `adjustResize` set in the manifest so the webview shrinks
  // on keyboard open — but the scroll position stays, which means the
  // last couple of bubbles slide off the top of the composer and users
  // have to manually swipe up. Watching visualViewport.resize fires in
  // both native (Capacitor WebView) and web browsers, so one listener
  // covers both platforms. We also re-run on `scroll` because iOS keeps
  // the webview height stable and only shifts visualViewport.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return undefined;
    const vv = window.visualViewport;
    let keyboardOpen = false;
    const baselineHeight = vv.height;
    const handleViewportChange = () => {
      const container = scrollContainerRef.current;
      if (!container) return;
      // Keyboard considered open when the visual viewport is >=120px
      // shorter than the baseline (window height at mount). Threshold
      // avoids false positives from browser chrome collapse.
      const isOpen = vv.height < baselineHeight - 120;
      if (isOpen && !keyboardOpen) {
        keyboardOpen = true;
        // Two frames to catch the layout settling after the resize.
        requestAnimationFrame(() => {
          container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
          requestAnimationFrame(() => {
            container.scrollTop = container.scrollHeight;
          });
        });
      } else if (!isOpen && keyboardOpen) {
        keyboardOpen = false;
      }
    };
    vv.addEventListener('resize', handleViewportChange);
    vv.addEventListener('scroll', handleViewportChange);
    return () => {
      vv.removeEventListener('resize', handleViewportChange);
      vv.removeEventListener('scroll', handleViewportChange);
    };
  }, []);

  return (
    <div
      className="app-shell premium-page"
      data-testid={pageTestId}
      style={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: 'var(--app-bg)',
        // Standard messenger UX: when a bottom nav is provided we reserve
        // space for it; when it's omitted (inside a 1:1 conversation) we
        // only pad the safe-area so the composer can hug the keyboard.
        paddingBottom: bottomNav
          ? 'calc(var(--nav-height, 88px) + env(safe-area-inset-bottom, 0px) + 10px)'
          : 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {/* Compact sticky top bar — back + avatar + name/subtitle + call + bell */}
      <div
        data-testid={`${pageTestId}-topbar`}
        style={{
          flexShrink: 0,
          padding: '10px 14px 10px',
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto',
          alignItems: 'center',
          gap: 10,
          background: 'var(--surface)',
          borderBottom: '1px solid var(--card-border)',
          boxShadow: '0 2px 12px -8px rgba(17,17,26,0.16)',
          position: 'relative',
          zIndex: 3,
        }}
      >
        <button
          onClick={onBack}
          data-testid={backTestId}
          aria-label="Back"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ChevronLeft size={28} color="var(--text)" strokeWidth={1.8} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <MessengerAvatar
            name={contactName}
            imageUrl={contactImage}
            size={38}
            textSize={14}
            dataTestId={contactTestId}
          />
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <p
              data-testid={`${pageTestId}-contact-name`}
              style={{
                margin: 0,
                fontFamily: "'Inria Serif', serif",
                fontSize: 17,
                fontWeight: 700,
                color: 'var(--text)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.2,
              }}
            >
              {contactName}
            </p>
            {contactSubtitle ? (
              <p
                data-testid={`${pageTestId}-contact-subtitle`}
                style={{
                  margin: '1px 0 0',
                  fontFamily: "'Inria Serif', serif",
                  fontSize: 12,
                  color: 'var(--text-2)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: 1.2,
                }}
              >
                {contactSubtitle}
              </p>
            ) : null}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {showCallButton ? (
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={onCall}
              data-testid={callTestId}
              aria-label={callBadge ? 'Voice call (missed call in this chat)' : 'Voice call'}
              style={{
                position: 'relative',
                width: 40,
                height: 40,
                borderRadius: '50%',
                border: '1px solid var(--card-border)',
                background: 'var(--accent-surface)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Phone size={18} color="var(--accent-purple-strong, #6335e0)" />
              {callBadge ? (
                // Small red dot in the top-right of the phone button —
                // visual cue that an unread missed-call chip is waiting
                // in this thread. Clears automatically once the messages
                // are marked read by the existing /messages/mark-read
                // pipeline (fires on chat open).
                <span
                  data-testid={callTestId ? `${callTestId}-missed-badge` : undefined}
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: '#E94C57',
                    border: '2px solid var(--surface)',
                    boxShadow: '0 0 6px rgba(233, 76, 87, 0.45)',
                  }}
                />
              ) : null}
            </motion.button>
          ) : null}
          {bellNode}
        </div>
      </div>

      {/* Chat surface — full-bleed patterned area for message flow */}
      <div style={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
        {/* Subtle geometric pattern behind messages — very low opacity so it
            feels like premium paper without competing with content */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.5,
            backgroundImage: 'radial-gradient(rgba(112, 63, 246, 0.055) 1px, transparent 1px)',
            backgroundSize: '18px 18px',
            zIndex: 0,
          }}
        />

        {pinnedNode ? <div style={{ position: 'relative', zIndex: 2 }}>{pinnedNode}</div> : null}

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
          ref={scrollContainerRef}
          onScroll={handleScroll}
          data-testid={`${pageTestId}-messages-scroll`}
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: '14px 14px 4px',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {loading ? (
            <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, color: 'var(--text-2)', textAlign: 'center', paddingTop: 20 }}>Loading chat...</p>
          ) : null}
          {!loading && !messages.length ? (
            <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, color: 'var(--text-2)', textAlign: 'center', paddingTop: 20 }}>{emptyText}</p>
          ) : null}
          {!loading ? messages.map(renderMessage) : null}
          <div ref={bottomRef} />
        </motion.div>

        <AnimatePresence>
          {showScrollToBottom ? (
            <motion.button
              key="scroll-to-bottom"
              type="button"
              onClick={scrollToBottom}
              data-testid={`${pageTestId}-scroll-to-bottom-button`}
              aria-label="Scroll to latest message"
              initial={{ opacity: 0, scale: 0.7, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.7, y: 12 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              style={{
                position: 'absolute',
                right: 18,
                bottom: 92,
                width: 42,
                height: 42,
                borderRadius: '50%',
                background: 'var(--surface)',
                border: '1px solid var(--card-border)',
                boxShadow: '0 8px 22px -8px rgba(17,17,26,0.28)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 3,
              }}
            >
              <ChevronDown size={20} color="var(--accent-purple-strong, #6335e0)" />
            </motion.button>
          ) : null}
        </AnimatePresence>

        {/* Composer sits above the bottom nav */}
        <div style={{ flexShrink: 0, padding: '8px 12px 10px', position: 'relative', zIndex: 2 }}>
          {composer}
        </div>
      </div>

      {bottomNav}
    </div>
  );
};
